import { useEffect } from 'react';

/**
 * Timestamp du dernier clic de l'utilisateur.
 * Variable module-level (partagée, pas de re-render).
 * Initialisée à maintenant pour ne pas pénaliser la première connexion.
 */
let lastActivityTime: number = Date.now();

/**
 * Retourne le timestamp (ms) du dernier clic utilisateur.
 */
export const getLastActivityTime = (): number => lastActivityTime;

/** Décode le payload d'un JWT sans vérification de signature */
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
};

/**
 * Calcule le seuil d'inactivité dynamiquement depuis le token : (exp - iat) * 1000.
 * Lit access_token en priorité, puis id_token en fallback.
 * Retourne 30 minutes si aucun token n'est décodable.
 */
export const getInactivityThresholdMs = (): number => {
    const sso = JSON.parse(localStorage.getItem('sso-response') ?? 'null');
    const rawToken = sso?.access_token ?? sso?.id_token;
    const payload = rawToken ? decodeJwtPayload(rawToken) : null;
    if (payload?.exp && payload?.iat) {
        return ((payload.exp as number) - (payload.iat as number)) * 1000;
    }
    return 30 * 60 * 1000; // fallback 30min
};

/**
 * Hook qui écoute les clics sur le document et met à jour
 * le timestamp d'activité. À monter une seule fois dans l'app
 * (ex: PrivateRoute / Navigation).
 */
function useUserActivity() {
    useEffect(() => {
        const handler = () => {
            lastActivityTime = Date.now();
        };
        document.addEventListener('click', handler, { passive: true });
        return () => document.removeEventListener('click', handler);
    }, []);
}

export { useUserActivity };
